// ─── Search Orchestrator ────────────────────────────────────────────────────
//
// Manages all state for search ad previews: form data, media files, save/load,
// delete, approve, dirty tracking. Renders SearchFormRenderer + MockupRouter.

import { useReducer, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Lock } from 'lucide-react';
import { useCrossVariantStatus } from '@/hooks/useCrossVariantStatus';
import {
  useVariantValidationStatus,
  ValidationStatusIcon,
} from '@/hooks/useVariantValidationStatus';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getSearchAssets,
  submitSearchAssets,
  deleteSearchVariant,
  duplicateSearchVersion,
  approveAllSearchVariants,
  type SearchAsset,
  type MediaFileEntry,
} from '@/api/searchAssets';
import { uploadSingleFile, type Campaign } from '@/api/campaigns';
import { searchAssetContentSignature } from './searchAssetContentSignature';
import { useGearExchangeSearch } from '@/hooks/useGearExchangeSearch';
import { useFormatValidationConfig } from '@/api/searchValidationConfig';
import type { SearchFormState, MediaFileItem, ImageExtension } from './shared';
import { validateSearchForm } from './shared';
import { mergeBackendConfig } from './validation/mergeBackendConfig';
import type { FieldIssue } from '../../types';
import type { SearchFormatType, SearchFormatConfig } from './search-config';
import { SEARCH_FORMAT_CONFIGS } from './search-config';
import { SearchFormRenderer } from './SearchFormRenderer';
import { MockupRouter } from './mockups';

// ─── Initial state from config ──────────────────────────────────────────────

export function createInitialSearchFormState(config: SearchFormatConfig): SearchFormState {
  return {
    headlines: config.headlines ? Array(config.headlines.min).fill('') : [],
    longHeadlines: config.longHeadlines ? Array(config.longHeadlines.min).fill('') : [],
    descriptions: config.descriptions ? Array(config.descriptions.min).fill('') : [],
    displayUrlPaths: config.displayUrlPaths
      ? Array(Math.max(config.displayUrlPaths.min, 0)).fill('')
      : [],
    cta: config.cta ? Array(config.cta.min).fill('') : [],
    finalUrl: '',
    businessName: '',
    legal: '',
    sitelinks: [],
    isOfferLed: false,
    offerEndDate: '',
  };
}

// ─── Reducer ────────────────────────────────────────────────────────────────

type SearchFormAction =
  | { type: 'patch'; payload: Partial<SearchFormState> }
  | { type: 'reset'; payload: SearchFormState };

function searchFormReducer(state: SearchFormState, action: SearchFormAction): SearchFormState {
  switch (action.type) {
    case 'patch':
      return { ...state, ...action.payload };
    case 'reset':
      return action.payload;
  }
}

/** Convert a SearchAsset from the API into form state. */
function assetToFormState(asset: SearchAsset, config: SearchFormatConfig): SearchFormState {
  const initial = createInitialSearchFormState(config);
  return {
    headlines: asset.headlines?.length ? asset.headlines : initial.headlines,
    longHeadlines: asset.long_headlines?.length ? asset.long_headlines : initial.longHeadlines,
    descriptions: asset.descriptions?.length ? asset.descriptions : initial.descriptions,
    displayUrlPaths: asset.display_url_paths?.length
      ? asset.display_url_paths
      : initial.displayUrlPaths,
    cta: asset.cta?.length ? asset.cta : initial.cta,
    finalUrl: asset.final_url ?? '',
    businessName: asset.business_name ?? '',
    legal: asset.legal ?? '',
    sitelinks: asset.sitelinks?.length
      ? asset.sitelinks.map((s) => ({
          title: s.title ?? '',
          description1: s.description1 ?? '',
          description2: s.description2 ?? '',
          finalUrl: s.final_url ?? '',
        }))
      : [],
    isOfferLed: !!asset.is_offer_led,
    offerEndDate: asset.offer_end_date ?? '',
  };
}

// ─── Shared field-name normalization (backend → frontend) ────────────────
// Backend uses "headlines[0]" → frontend expects "headline_0" (singular + underscore).
const BACKEND_FIELD_MAP: Record<string, string> = {
  headlines: 'headline',
  long_headlines: 'long_headline',
  post_copies: 'long_headline',
  descriptions: 'description',
  display_url_paths: 'display_url_path',
};

function normalizeBackendField(field: string): string {
  const match = field.match(/^(\w+)\[(\d+)\]$/);
  if (match) {
    const singular = BACKEND_FIELD_MAP[match[1]];
    if (singular) return `${singular}_${match[2]}`;
  }
  return field;
}

// ─── Shared helper: merge backend validation_result into field issues ─────
// Used both when restoring on refresh and after a fresh validate call.
function mergeBackendIssues(
  stored: {
    errors?: { field: string; message: string }[];
    warnings?: { field: string; message: string }[];
  },
  existing: Record<string, FieldIssue>,
): Record<string, FieldIssue> {
  const issues = { ...existing };
  const addToField = (field: string, type: 'error' | 'warning', message: string) => {
    const key = normalizeBackendField(field);
    // If the frontend already produced issues of this type for this field,
    // skip the backend message to avoid duplicates with different wording
    const frontendEntry = existing[key];
    if (frontendEntry) {
      const frontendList = type === 'error' ? frontendEntry.errors : frontendEntry.warnings;
      if (frontendList.length > 0) return;
    }
    if (!issues[key]) issues[key] = { errors: [], warnings: [] };
    const list = type === 'error' ? issues[key].errors : issues[key].warnings;
    if (!list.includes(message)) list.push(message);
  };
  for (const e of stored.errors ?? []) addToField(e.field, 'error', e.message);
  for (const w of stored.warnings ?? []) addToField(w.field, 'warning', w.message);
  return issues;
}

let mediaIdCounter = 1;
function makeMediaId(prefix: string) {
  return `${prefix}-${mediaIdCounter++}`;
}

/** Create initial media file items from config minimums. */
function createInitialMediaFiles(config: SearchFormatConfig): MediaFileItem[] {
  if (!config.mediaSlots) return [];
  const items: MediaFileItem[] = [];
  for (const slotConfig of Object.values(config.mediaSlots)) {
    if (slotConfig.slot !== 'youtube_video') continue;
    for (let i = 0; i < slotConfig.min; i++) {
      items.push({
        id: makeMediaId(slotConfig.slot),
        slot: slotConfig.slot,
        file: null,
        saved: null,
        url: '',
        expirationDate: '',
      });
    }
  }
  return items;
}

/** Ensure loaded media files meet config minimums per slot. */
function ensureMediaMinimums(loaded: MediaFileItem[], config: SearchFormatConfig): MediaFileItem[] {
  if (!config.mediaSlots) return loaded;
  const result = [...loaded];
  for (const slotConfig of Object.values(config.mediaSlots)) {
    if (slotConfig.slot !== 'youtube_video') continue;
    const existing = result.filter((f) => f.slot === slotConfig.slot).length;
    for (let i = existing; i < slotConfig.min; i++) {
      result.push({
        id: makeMediaId(slotConfig.slot),
        slot: slotConfig.slot,
        file: null,
        saved: null,
        url: '',
        expirationDate: '',
      });
    }
  }
  return result;
}

/** Serialize form state to the shape the submit API expects. */
function formStateToPayload(state: SearchFormState, variantNumber: number) {
  return {
    headlines: state.headlines,
    long_headlines: state.longHeadlines,
    descriptions: state.descriptions,
    cta: state.cta,
    legal: state.legal,
    business_name: state.businessName,
    display_url_paths: state.displayUrlPaths,
    final_url: state.finalUrl,
    // Drop fully-empty rows (blank UI placeholders) before persisting.
    // Trim each field so stored values match what validation checked.
    sitelinks: state.sitelinks
      .filter(
        (s) =>
          s.title.trim() || s.description1.trim() || s.description2.trim() || s.finalUrl.trim(),
      )
      .map((s) => ({
        title: s.title.trim(),
        description1: s.description1.trim(),
        description2: s.description2.trim(),
        final_url: s.finalUrl.trim(),
      })),
    variant_number: variantNumber,
    is_offer_led: state.isOfferLed,
    offer_end_date: state.offerEndDate,
  };
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface SearchPreviewAndAdDetailsProps {
  formatType: SearchFormatType;
  campaignId?: string;
  campaign?: Campaign | null;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  readOnly?: boolean;
  saveFnRef?: React.MutableRefObject<(() => Promise<void>) | undefined>;
  validateFnRef?: React.MutableRefObject<(() => Promise<void>) | undefined>;
  approveFnRef?: React.MutableRefObject<((note: string) => Promise<void>) | undefined>;
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
  onValidatingChange?: (validating: boolean) => void;
  onApprovingChange?: (approving: boolean) => void;
  onHasValidatedChange?: (hasValidated: boolean) => void;
  onIsApprovedChange?: (isApproved: boolean) => void;
  onAnyVariantHasErrorsChange?: (hasErrors: boolean) => void;
  onValidationResult?: (issues: Record<string, FieldIssue>) => void;
  onValidationComplete?: (hasErrors: boolean, hasWarnings: boolean) => void;
  onLockedChange?: (locked: boolean) => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function SearchOrchestrator({
  formatType,
  campaignId,
  campaign,
  showErrors,
  showWarnings,
  fieldIssues,
  readOnly = false,
  saveFnRef,
  validateFnRef,
  approveFnRef,
  onDirtyChange,
  onSavingChange,
  onValidatingChange,
  onApprovingChange,
  onHasValidatedChange,
  onIsApprovedChange,
  onAnyVariantHasErrorsChange,
  onValidationResult,
  onValidationComplete,
  onLockedChange,
}: SearchPreviewAndAdDetailsProps) {
  const config = SEARCH_FORMAT_CONFIGS[formatType];
  const { data: backendValidationConfig } = useFormatValidationConfig(formatType);
  // Merge backend validation rules on top of frontend config (backend = source of truth)
  const validationConfig = useMemo(
    () => mergeBackendConfig(config, backendValidationConfig),
    [config, backendValidationConfig],
  );
  const queryClient = useQueryClient();
  const isSavingRef = useRef(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const pendingVariantRef = useRef<number | null>(null);

  // ── Form state ────────────────────────────────────────────────────────────
  const [state, dispatch] = useReducer(searchFormReducer, config, createInitialSearchFormState);
  const [selectedVariant, setSelectedVariant] = useState(1);

  // ── Image extensions state (RSA) ──────────────────────────────────────────
  const [imageExtensions, setImageExtensions] = useState<ImageExtension[]>([]);
  const [loadedImageExtensionsSnapshot, setLoadedImageExtensionsSnapshot] = useState<string>('[]');

  // ── Generic media files state (PMax, Demand Gen, etc.) ────────────────────
  const [mediaFiles, setMediaFiles] = useState<MediaFileItem[]>([]);
  const [loadedMediaFilesSnapshot, setLoadedMediaFilesSnapshot] = useState<string>('[]');

  // Snapshot of loaded values for dirty tracking
  const [loadedSnapshot, setLoadedSnapshot] = useState<string | null>(null);

  // ── Gear Exchange search (cached for all media slots) ─────────────────────
  const { gxData, gxLoading, gxError } = useGearExchangeSearch(
    campaignId,
    campaign?.fordshare_entries,
  );

  // ── Fetch search assets from API ──────────────────────────────────────────
  const { data: assetsData, isLoading } = useQuery({
    queryKey: ['search-assets', campaignId],
    queryFn: () => getSearchAssets(campaignId!, true),
    enabled: !!campaignId,
  });

  // Find the asset for the selected variant
  const assetForVariant = useMemo(() => {
    if (!assetsData?.assets) return undefined;
    return assetsData.assets.find((a) => a.variant_number === selectedVariant);
  }, [assetsData, selectedVariant]);

  // Drives the load + validation-restore effects below instead of the
  // `assetForVariant` object identity, so refetches that change nothing those
  // effects read (approve, and any unrelated invalidation) don't re-reset the
  // form and re-download every media file for dimension detection.
  const assetSignature = useMemo(
    () => searchAssetContentSignature(assetForVariant),
    [assetForVariant],
  );

  // ── Lock state for selected version ───────────────────────────────────────
  const isVersionLocked = !!assetForVariant?.is_locked;
  useEffect(() => {
    onLockedChange?.(isVersionLocked);
  }, [isVersionLocked, onLockedChange]);

  // ── Cross-variant validation / approval state (mirrors social) ───────────
  useCrossVariantStatus(assetsData?.assets, {
    onHasValidatedChange,
    onIsApprovedChange,
    onAnyVariantHasErrorsChange,
  });

  // ── Load API data into form ───────────────────────────────────────────────
  useEffect(() => {
    if (assetForVariant) {
      const formState = assetToFormState(assetForVariant, config);
      dispatch({ type: 'reset', payload: formState });
      setLoadedSnapshot(JSON.stringify(formState));

      // Load image extensions from media_files (RSA)
      const loadedExtensions: ImageExtension[] = (assetForVariant.media_files || [])
        .filter((mf) => mf.slot === 'image_extension')
        .map((mf, i) => ({
          id: `saved-${i}`,
          file: null,
          saved: mf,
          sourceFordshareId: mf.source_fordshare_id ?? '',
          expirationDate: mf.expiration_date ?? '',
        }));
      setImageExtensions(loadedExtensions);
      setLoadedImageExtensionsSnapshot(
        JSON.stringify(
          loadedExtensions.map((e) => ({
            sourceFordshareId: e.sourceFordshareId,
            expirationDate: e.expirationDate,
            hasSaved: !!e.saved,
          })),
        ),
      );

      // Load generic media files (PMax images, logos, YouTube URLs)
      const savedMedia: MediaFileItem[] = (assetForVariant.media_files || [])
        .filter((mf) => mf.slot !== 'image_extension')
        .map((mf, i) => ({
          id: mf.id ?? `saved-media-${i}`,
          slot: mf.slot,
          file: null,
          saved: mf,
          url: mf.slot === 'youtube_video' ? mf.storage_path : undefined,
          expirationDate: mf.expiration_date ?? undefined,
          width: mf.width ?? undefined,
          height: mf.height ?? undefined,
        }));
      // Ensure minimums are met — add empty items for slots below their minimum
      const loadedMedia = ensureMediaMinimums(savedMedia, config);
      setMediaFiles(loadedMedia);
      setLoadedMediaFilesSnapshot(
        JSON.stringify(
          loadedMedia.map((m) => ({
            slot: m.slot,
            hasSaved: !!m.saved,
            url: m.url,
            expirationDate: m.expirationDate,
          })),
        ),
      );

      // Detect dimensions for saved media (so validation works without opening accordions)
      loadedMedia.forEach((item) => {
        if (!item.saved?.signed_url || item.slot === 'youtube_video') return;
        if (item.saved.content_type?.startsWith('video/')) {
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.muted = true;
          video.crossOrigin = 'anonymous';
          video.src = item.saved.signed_url;
          video.currentTime = 0.1;
          video.addEventListener('seeked', () =>
            setMediaFiles((prev) =>
              prev.map((f) =>
                f.id === item.id ? { ...f, width: video.videoWidth, height: video.videoHeight } : f,
              ),
            ),
          );
          video.load();
        } else {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () =>
            setMediaFiles((prev) =>
              prev.map((f) =>
                f.id === item.id ? { ...f, width: img.naturalWidth, height: img.naturalHeight } : f,
              ),
            );
          img.src = item.saved.signed_url;
        }
      });
    } else {
      const initial = createInitialSearchFormState(config);
      dispatch({ type: 'reset', payload: initial });
      setLoadedSnapshot(JSON.stringify(initial));
      setImageExtensions([]);
      setLoadedImageExtensionsSnapshot('[]');
      const initialMedia = createInitialMediaFiles(config);
      setMediaFiles(initialMedia);
      setLoadedMediaFilesSnapshot(
        JSON.stringify(
          initialMedia.map((m) => ({
            slot: m.slot,
            hasSaved: !!m.saved,
            url: m.url,
            expirationDate: m.expirationDate,
          })),
        ),
      );
    }

    // Clear field issues from previous variant so they don't bleed across versions
    onValidationResult?.({});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetSignature, formatType]);

  // ── Restore persisted validation_result from DB on load ─────────────────
  // Mirrors the social flow in PreviewAndAdDetails: on initial load (or variant
  // switch), map the stored validation_result into fieldIssues so errors/warnings
  // survive a page refresh without re-validating.
  // Also re-runs frontend-only checks (aspect ratios, char limits) so they persist.
  //
  // IMPORTANT: We derive formState and media directly from assetForVariant here
  // rather than using React state (state, mediaFiles, imageExtensions), because
  // those are set by the load useEffect above and may not have propagated yet
  // (both effects trigger on the same assetForVariant change).
  useEffect(() => {
    if (!assetForVariant?.validation_result) return;

    const stored = assetForVariant.validation_result;

    // Derive form state and media from the asset directly (avoids stale React state)
    const restoredState = assetToFormState(assetForVariant, config);

    const savedMedia: MediaFileItem[] = (assetForVariant.media_files || [])
      .filter((mf) => mf.slot !== 'image_extension')
      .map((mf, i) => ({
        id: mf.id ?? `restore-media-${i}`,
        slot: mf.slot,
        file: null,
        saved: mf,
        url: mf.slot === 'youtube_video' ? mf.storage_path : undefined,
        expirationDate: mf.expiration_date ?? undefined,
        width: mf.width ?? undefined,
        height: mf.height ?? undefined,
      }));
    const restoredMedia = ensureMediaMinimums(savedMedia, config);

    const restoredExtensions: ImageExtension[] = (assetForVariant.media_files || [])
      .filter((mf) => mf.slot === 'image_extension')
      .map((mf, i) => ({
        id: `restore-ext-${i}`,
        file: null,
        saved: mf,
        sourceFordshareId: mf.source_fordshare_id ?? '',
        expirationDate: mf.expiration_date ?? '',
      }));

    // Re-run frontend-only validation so aspect ratios, char limits, etc. persist
    const clientIssues = validateSearchForm(
      validationConfig,
      restoredState,
      restoredMedia,
      restoredExtensions,
    );

    // Merge backend issues on top (deduplicating)
    const issues = mergeBackendIssues(stored, clientIssues);

    const hasErrors = Object.values(issues).some((fi) => fi.errors.length > 0);
    const hasWarnings = Object.values(issues).some((fi) => fi.warnings.length > 0);

    onValidationResult?.(issues);
    onValidationComplete?.(hasErrors, hasWarnings);
    // `validationConfig` is a real dep, not padding: it starts as the frontend-only
    // config and is replaced once useFormatValidationConfig resolves the backend
    // rules. Without it the restored issues would be computed from the weaker
    // frontend rules and never recomputed. It is memoized on
    // [config, backendValidationConfig], so it settles and does not cause churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetSignature, validationConfig]);

  // ── Dirty tracking ────────────────────────────────────────────────────────
  const isDirty = useMemo(() => {
    // Check if any image extension has a new file or was removed/added
    const hasNewImageFiles = imageExtensions.some((e) => e.file !== null);
    const currentExtSnap = JSON.stringify(
      imageExtensions.map((e) => ({
        sourceFordshareId: e.sourceFordshareId,
        expirationDate: e.expirationDate,
        hasSaved: !!e.saved,
      })),
    );
    if (hasNewImageFiles || currentExtSnap !== loadedImageExtensionsSnapshot) return true;
    // Check generic media files (PMax images, logos, YouTube)
    const hasNewMediaFiles = mediaFiles.some((f) => f.file !== null);
    const currentMediaSnap = JSON.stringify(
      mediaFiles.map((m) => ({
        slot: m.slot,
        hasSaved: !!m.saved,
        url: m.url,
        expirationDate: m.expirationDate,
      })),
    );
    if (hasNewMediaFiles || currentMediaSnap !== loadedMediaFilesSnapshot) return true;
    if (!loadedSnapshot) return false;
    return JSON.stringify(state) !== loadedSnapshot;
  }, [
    state,
    loadedSnapshot,
    imageExtensions,
    loadedImageExtensionsSnapshot,
    mediaFiles,
    loadedMediaFilesSnapshot,
  ]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // ── Change handler ────────────────────────────────────────────────────────
  const handleChange = useCallback((patch: Partial<SearchFormState>) => {
    dispatch({ type: 'patch', payload: patch });
  }, []);

  // ── Save handler ──────────────────────────────────────────────────────────
  async function handleSave(skipValidation: boolean = true): Promise<boolean> {
    if (!campaignId || isSavingRef.current) return false;
    if (isVersionLocked) {
      toast.error('This version is locked because the campaign is live');
      return false;
    }
    isSavingRef.current = true;
    onSavingChange?.(true);
    try {
      const payload = formStateToPayload(state, selectedVariant);

      // Build media_files array — upload new files, keep existing saved ones
      const allMediaFiles: MediaFileEntry[] = [];

      // RSA image extensions
      for (const ext of imageExtensions) {
        if (ext.file) {
          const uploadResult = await uploadSingleFile(campaignId, ext.file);
          allMediaFiles.push({
            id: ext.id,
            slot: 'image_extension',
            storage_path: uploadResult.storage_path,
            content_type: uploadResult.content_type,
            file_size: uploadResult.file_size,
            source_fordshare_id: ext.sourceFordshareId || null,
            expiration_date: ext.expirationDate || null,
          });
        } else if (ext.saved) {
          allMediaFiles.push({
            ...ext.saved,
            source_fordshare_id: ext.sourceFordshareId || null,
            expiration_date: ext.expirationDate || null,
          });
        }
      }

      // Generic media files (PMax images, logos, YouTube URLs)
      for (const item of mediaFiles) {
        if (item.slot === 'youtube_video') {
          // YouTube URLs — no file upload, just store the URL as storage_path
          if (item.url?.trim()) {
            allMediaFiles.push({
              id: item.id,
              slot: item.slot,
              storage_path: item.url.trim(),
              content_type: 'text/url',
              file_size: 0,
              expiration_date: item.expirationDate || null,
            });
          }
        } else if (item.file) {
          // New image/logo file — upload first
          const uploadResult = await uploadSingleFile(campaignId, item.file);
          allMediaFiles.push({
            id: item.id,
            slot: item.slot,
            storage_path: uploadResult.storage_path,
            content_type: uploadResult.content_type,
            file_size: uploadResult.file_size,
            width: item.width,
            height: item.height,
          });
        } else if (item.saved) {
          // Existing saved file — keep it, overlay current dimensions
          allMediaFiles.push({
            ...item.saved,
            width: item.width ?? item.saved.width,
            height: item.height ?? item.saved.height,
          });
        }
      }

      await submitSearchAssets(campaignId, {
        ...payload,
        media_files: allMediaFiles,
        skip_validation: skipValidation,
      });

      // Update snapshot so isDirty resets
      setLoadedSnapshot(JSON.stringify(state));

      // Refresh asset data (will reload the signed URL for the uploaded video)
      await queryClient.invalidateQueries({ queryKey: ['search-assets', campaignId] });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });

      toast.success('Search ad saved');
      return true;
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save search ad');
      return false;
    } finally {
      isSavingRef.current = false;
      onSavingChange?.(false);
    }
  }

  // ── Approve handler ───────────────────────────────────────────────────────
  async function handleApprove(note: string) {
    if (!campaignId) return;
    onApprovingChange?.(true);
    try {
      // Save first to ensure latest data is persisted
      if (isDirty) {
        const saved = await handleSave();
        if (!saved) return; // Save blocked (e.g. locked version) — abort approve
      }

      await approveAllSearchVariants(campaignId, note);

      await queryClient.invalidateQueries({ queryKey: ['search-assets', campaignId] });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });

      onIsApprovedChange?.(true);
      toast.success('Search ad approved');
    } catch (err) {
      console.error('Approve failed:', err);
      toast.error('Failed to approve search ad');
    } finally {
      onApprovingChange?.(false);
    }
  }

  // ── Delete all handler ────────────────────────────────────────────────────
  async function handleDeleteAll() {
    if (!campaignId) return;
    // Block delete-all when any version is locked (campaign is live)
    const hasLockedVersions = assetsData?.assets?.some((a) => a.is_locked);
    if (hasLockedVersions) {
      toast.error('Cannot delete assets — some versions are locked because the campaign is live');
      return;
    }
    setIsDeletingAll(true);
    try {
      // Delete all variants
      if (assetsData?.assets) {
        const variantNumbers = [...new Set(assetsData.assets.map((a) => a.variant_number))];
        for (const vn of variantNumbers) {
          await deleteSearchVariant(campaignId, vn);
        }
      }

      // Reset form to initial state
      const initial = createInitialSearchFormState(config);
      dispatch({ type: 'reset', payload: initial });
      setLoadedSnapshot(JSON.stringify(initial));
      setImageExtensions([]);
      setLoadedImageExtensionsSnapshot('[]');
      setMediaFiles([]);
      setLoadedMediaFilesSnapshot('[]');

      await queryClient.invalidateQueries({ queryKey: ['search-assets', campaignId] });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });

      onIsApprovedChange?.(false);
      onValidationResult?.({});
      onHasValidatedChange?.(false);
      onAnyVariantHasErrorsChange?.(false);
      toast.success('All search assets deleted');
      setShowDeleteAllConfirm(false);
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete search assets');
    } finally {
      setIsDeletingAll(false);
    }
  }

  // ── Duplicate (add version) handler ─────────────────────────────────────
  async function handleDuplicateVersion() {
    if (!campaignId || isDuplicating) return;
    setIsDuplicating(true);
    try {
      const result = await duplicateSearchVersion(campaignId, selectedVariant);
      toast.success(result.message);
      // Switch to the newly created version using structured field
      if (result.new_version) {
        pendingVariantRef.current = result.new_version;
        setSelectedVariant(result.new_version);
      }
      await queryClient.invalidateQueries({ queryKey: ['search-assets', campaignId] });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    } catch (err: unknown) {
      console.error('Duplicate failed:', err);
      const message = err instanceof Error ? err.message : 'Failed to duplicate version';
      toast.error(message);
    } finally {
      setIsDuplicating(false);
    }
  }

  // ── Delete single version handler ────────────────────────────────────────
  async function handleDeleteVersion(variantNumber: number) {
    if (!campaignId) return;
    try {
      await deleteSearchVariant(campaignId, variantNumber);
      await queryClient.invalidateQueries({ queryKey: ['search-assets', campaignId] });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      // If we deleted the selected variant, switch to first available
      if (variantNumber === selectedVariant) {
        setSelectedVariant(1);
      }
      toast.success(`Version ${variantNumber} deleted`);
    } catch (err) {
      console.error('Delete version failed:', err);
      toast.error('Failed to delete version');
    }
  }

  // ── Validate handler ─────────────────────────────────────────────────────
  async function handleValidate() {
    if (!campaignId) return;

    // Always save with validation — this triggers backend validation and stores validation_result.
    // Even when !isDirty, the backend needs to run validation (FordShare check, etc.)
    const saveSuccess = await handleSave(false);
    if (!saveSuccess) {
      // Save failed — abort validation to avoid showing stale/incorrect results
      return;
    }

    onValidatingChange?.(true);
    try {
      // Run client-side validation for immediate media/UI checks
      const clientIssues = validateSearchForm(validationConfig, state, mediaFiles, imageExtensions);

      // Merge in backend validation_result (persisted on last save).
      // Re-read the latest asset data after potential save above.
      const latestData = await queryClient.fetchQuery({
        queryKey: ['search-assets', campaignId],
        queryFn: () => getSearchAssets(campaignId!, true),
      });
      const latestAsset = latestData?.assets?.find((a) => a.variant_number === selectedVariant);
      const stored = latestAsset?.validation_result;

      if (stored) {
        const merged = mergeBackendIssues(stored, clientIssues);
        // Copy merged results back into clientIssues object
        for (const [key, value] of Object.entries(merged)) {
          clientIssues[key] = value;
        }
      }

      onValidationResult?.(clientIssues);

      const hasErrors = Object.values(clientIssues).some((fi) => fi.errors.length > 0);
      const hasWarnings = Object.values(clientIssues).some((fi) => fi.warnings.length > 0);
      onValidationComplete?.(hasErrors, hasWarnings);

      if (hasErrors) {
        toast.error('Validation failed — check highlighted fields');
      } else if (hasWarnings) {
        toast.success('Validation passed with warnings');
      } else {
        toast.success('Validation passed');
      }
    } finally {
      onValidatingChange?.(false);
    }
  }

  // ── Wire up refs ──────────────────────────────────────────────────────────
  if (saveFnRef)
    saveFnRef.current = async () => {
      await handleSave();
    };
  if (validateFnRef) validateFnRef.current = handleValidate;
  if (approveFnRef) approveFnRef.current = handleApprove;

  // ── Has data check (for Delete All visibility) ────────────────────────────
  const hasAssets = (assetsData?.total_assets ?? 0) > 0;

  // ── Available variants for version selector ──────────────────────────────
  const availableVariants = useMemo(() => {
    if (!assetsData?.assets?.length) return [1];
    const variants = [...new Set(assetsData.assets.map((a) => a.variant_number))].sort(
      (a, b) => a - b,
    );
    return variants.length ? variants : [1];
  }, [assetsData]);

  const MAX_SEARCH_VERSIONS = 13;
  const canAddVersion = availableVariants.length < MAX_SEARCH_VERSIONS && hasAssets;

  // Per-version validation status for tab icons
  const versionValidationStatus = useVariantValidationStatus(assetsData?.assets, availableVariants);

  // Keep selectedVariant in sync with available variants
  useEffect(() => {
    // If we're waiting for a newly created variant to appear, don't reset
    if (pendingVariantRef.current !== null) {
      if (availableVariants.includes(pendingVariantRef.current)) {
        pendingVariantRef.current = null; // arrived, clear pending
      }
      return;
    }
    if (!availableVariants.includes(selectedVariant)) {
      setSelectedVariant(availableVariants[0] ?? 1);
    }
  }, [availableVariants, selectedVariant]);

  return (
    <>
      {/* ── Delete all confirmation modal ──────────────────────────────────── */}
      {showDeleteAllConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-80 bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex-shrink-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Delete All Assets
              </p>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-600">
                This will permanently delete{' '}
                <span className="font-semibold text-slate-800">all assets and variants</span> for
                this campaign. This action cannot be undone.
              </p>
              <div className="flex gap-2 mt-5 justify-end">
                <button
                  onClick={() => setShowDeleteAllConfirm(false)}
                  disabled={isDeletingAll}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAll}
                  disabled={isDeletingAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {isDeletingAll ? 'Deleting...' : 'Delete All'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 bg-white rounded-xl border border-slate-200 flex flex-col min-h-0 min-w-0 overflow-hidden">
        {/* ── Header ── */}
        <div className="px-4 h-11 border-b border-slate-200 bg-slate-50 rounded-t-xl flex items-center gap-2 flex-shrink-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 shrink-0">
            {config.label}
          </p>

          <div className="flex-1" />

          {/* Version selector — tab style matching social ads variants */}
          {availableVariants.length > 1 && (
            <div className="flex items-center">
              {availableVariants.map((v) => {
                const isVariantLocked = !!assetsData?.assets?.find((a) => a.variant_number === v)
                  ?.is_locked;
                return (
                  <div key={v} className="relative group/tab">
                    <button
                      onClick={async () => {
                        if (v === selectedVariant) return;
                        if (isDirty) {
                          await handleSave();
                          // Save failed — toast already shown, still switch
                        }
                        setSelectedVariant(v);
                      }}
                      className={`px-3 py-1 text-xs transition-all border-b-2 flex items-center gap-1 ${
                        v === selectedVariant
                          ? 'text-[#0014cc] font-semibold border-[#0014cc]'
                          : 'text-slate-500 font-medium border-transparent hover:text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      Version {v}
                      {isVariantLocked && <Lock className="w-3 h-3 text-slate-400" />}
                      <ValidationStatusIcon status={versionValidationStatus[v]} />
                    </button>
                    {!readOnly && !isVariantLocked && hasAssets && availableVariants.length > 1 && (
                      <button
                        onClick={() => handleDeleteVersion(v)}
                        aria-label={`Delete version ${v}`}
                        className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-slate-300 hover:bg-red-400 text-white flex items-center justify-center opacity-0 group-hover/tab:opacity-100 focus:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-red-500 transition-opacity"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Version button */}
          {!readOnly && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <button
                    onClick={canAddVersion ? handleDuplicateVersion : undefined}
                    disabled={!canAddVersion || isDuplicating}
                    className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg border transition-colors ${
                      canAddVersion
                        ? 'bg-white text-slate-600 border-slate-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200'
                        : 'bg-white text-slate-300 border-slate-100 cursor-not-allowed'
                    }`}
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    {isDuplicating ? 'Adding...' : 'Add Version'}
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {canAddVersion
                  ? 'Duplicate current version'
                  : !hasAssets
                    ? 'Save first to add versions'
                    : `Max ${MAX_SEARCH_VERSIONS} versions reached`}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Delete All button — only visible when there are saved assets and none are locked */}
          {!readOnly && hasAssets && !assetsData?.assets?.some((a) => a.is_locked) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setShowDeleteAllConfirm(true)}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg bg-white text-slate-500 border border-slate-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Delete All
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Delete all assets for this campaign</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* ── Two-column content area ── */}
        <div className="flex-1 flex min-h-0">
          {/* Left: Mockup preview — 43% width */}
          <div className="w-[43%] border-r border-slate-100 flex flex-col min-h-0">
            <div className="flex-1 overflow-auto hover-scroll min-h-0 px-1 py-3">
              <div className="flex flex-col items-center justify-center min-h-full">
                <MockupRouter formatType={formatType} />
              </div>
            </div>
            <p className="text-[10px] text-slate-400 text-center italic px-6 pb-3 leading-relaxed flex-shrink-0">
              This is a mock-up and does not represent exact platform previews.
            </p>
          </div>

          {/* Right: Ad details form — remaining width */}
          <div className="flex-1 flex flex-col min-h-0 min-w-0">
            {isLoading ? (
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                <div className="space-y-2">
                  <Skeleton className="h-2.5 w-20 rounded" />
                  <Skeleton className="h-3 w-full rounded" />
                  <Skeleton className="h-3 w-4/5 rounded" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-2.5 w-24 rounded" />
                  <Skeleton className="h-9 rounded-lg" />
                  <Skeleton className="h-9 rounded-lg" />
                  <Skeleton className="h-9 rounded-lg" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-2.5 w-24 rounded" />
                  <Skeleton className="h-9 rounded-lg" />
                </div>
              </div>
            ) : (
              <div
                className={`flex-1 flex flex-col min-h-0 min-w-0${readOnly || isVersionLocked ? ' opacity-60 [&_input]:pointer-events-none [&_select]:pointer-events-none [&_textarea]:pointer-events-none' : ''}`}
              >
                {isVersionLocked && (
                  <div className="mx-6 mt-4 mb-0 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                    <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>
                      This version is locked because the campaign is live. You can add a new version
                      to make changes.
                    </span>
                  </div>
                )}
                <SearchFormRenderer
                  config={config}
                  state={state}
                  onChange={handleChange}
                  showErrors={showErrors}
                  showWarnings={showWarnings}
                  fieldIssues={fieldIssues}
                  readOnly={readOnly || isVersionLocked}
                  imageExtensions={imageExtensions}
                  onImageExtensionsChange={setImageExtensions}
                  mediaFiles={mediaFiles}
                  onMediaFilesChange={setMediaFiles}
                  onMediaFileDimensionsLoaded={(id, width, height) =>
                    setMediaFiles((prev) =>
                      prev.map((f) => (f.id === id ? { ...f, width, height } : f)),
                    )
                  }
                  gxData={gxData}
                  gxLoading={gxLoading}
                  gxError={gxError}
                  campaign={campaign ?? null}
                  campaignId={campaignId}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
